import React from "react";
import { Document, Page, Text, View, StyleSheet, Image, Svg, Path, Font } from "@react-pdf/renderer";
import ctbLogo from "../../assets/ctbLogoBlue.png";

// Register fonts for the NDA PDF Document: Montserrat for metadata/labels, Lora for legal body text
Font.register({
  family: "Montserrat",
  fonts: [
    { src: "https://cdn.jsdelivr.net/fontsource/fonts/montserrat@latest/latin-300-normal.ttf", fontWeight: 300 }, // Light
    { src: "https://cdn.jsdelivr.net/fontsource/fonts/montserrat@latest/latin-400-normal.ttf", fontWeight: 400 }, // Regular
    { src: "https://cdn.jsdelivr.net/fontsource/fonts/montserrat@latest/latin-700-normal.ttf", fontWeight: 700 }  // Bold
  ]
});

Font.register({
  family: "Lora",
  fonts: [
    { src: "https://cdn.jsdelivr.net/fontsource/fonts/lora@latest/latin-400-normal.ttf", fontWeight: 400 }, // Regular
    { src: "https://cdn.jsdelivr.net/fontsource/fonts/lora@latest/latin-700-normal.ttf", fontWeight: 700 }  // Bold
  ]
});

// Use direct PNG import for reliable rendering in react-pdf
const LOGO_SRC = ctbLogo;

// ---------------------------------------------------------------------------
// Styles matching the reference screenshots precisely
// ---------------------------------------------------------------------------
const styles = StyleSheet.create({
  page: {
    paddingTop: 24,
    paddingBottom: 48,
    paddingHorizontal: 36,
    backgroundColor: "#ffffff",
    fontFamily: "Lora",
    fontSize: 9,
    color: "#1a1a1a"
  },
  // Header
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
    paddingBottom: 8,
    borderBottomWidth: 0.75,
    borderBottomColor: "#cccccc"
  },
  headerLeft: {
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start"
  },
  logoImage: {
    width: 96,
    height: 26,
    objectFit: "contain",
    marginLeft: -10 // Offset the transparent margin on the left of the PNG logo to align with the text below
  },
  logoTextRow: {
    flexDirection: "row",
    alignItems: "center"
  },
  logoTextMain: {
    fontSize: 12,
    fontFamily: "Montserrat",
    fontWeight: "bold",
    color: "#0f2d7a",
    letterSpacing: 0.3
  },
  ndaNumber: {
    fontSize: 7.5,
    fontFamily: "Montserrat",
    color: "#555555",
    marginTop: 4
  },
  confidentialBadge: {
    borderWidth: 0.5,
    borderColor: "#fca5a5",
    backgroundColor: "#fef2f2",
    borderRadius: 2,
    paddingHorizontal: 8,
    paddingVertical: 3,
    color: "#991b1b",
    fontSize: 7.5,
    fontFamily: "Montserrat",
    letterSpacing: 0.2,
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  // Title block
  mutualUndertaking: {
    fontSize: 9,
    fontFamily: "Montserrat",
    fontWeight: 600,
    color: "#84cc16",
    marginBottom: 4,
    marginTop: 10
  },
  mainTitle: {
    fontSize: 26,
    fontFamily: "Lora",
    fontWeight: "bold",
    color: "#0f172a",
    letterSpacing: -0.3,
    marginBottom: 10,
    lineHeight: 1.1
  },
  titleSubtitle: {
    fontSize: 9,
    fontFamily: "Lora",
    color: "#4f5e7b",
    lineHeight: 1.4,
    marginBottom: 12,
    maxWidth: 420
  },
  // Metadata row
  metaTableRow: {
    flexDirection: "row",
    borderTopWidth: 0.75,
    borderTopColor: "#cbd5e1",
    borderBottomWidth: 0.75,
    borderBottomColor: "#cbd5e1",
    marginTop: 10,
    marginBottom: 10
  },
  metaTableCell: { 
    flex: 1, 
    paddingRight: 16,
    paddingVertical: 8
  },
  metaCellDivider: {
    flex: 1,
    borderLeftWidth: 0.75,
    borderLeftColor: "#cbd5e1",
    paddingLeft: 16,
    paddingRight: 16,
    paddingVertical: 8
  },
  metaSmallLabel: {
    fontSize: 7,
    fontFamily: "Montserrat",
    color: "#8898aa",
    marginBottom: 3
  },
  metaValue: {
    fontSize: 9.5,
    fontFamily: "Montserrat",
    fontWeight: "bold",
    color: "#0f172a"
  },
  // Party block
  partyRow: {
    flexDirection: "row",
    marginTop: 8
  },
  partyCol: { 
    flex: 1, 
    paddingRight: 16,
    paddingVertical: 4
  },
  partyColRight: {
    flex: 1,
    paddingLeft: 24,
    paddingVertical: 4
  },
  partySmallLabel: {
    fontSize: 7,
    fontFamily: "Montserrat",
    color: "#8898aa",
    marginBottom: 4
  },
  partyName: {
    fontSize: 11.5,
    fontFamily: "Montserrat",
    fontWeight: "bold",
    color: "#0f172a",
    marginBottom: 4
  },
  partyDetail: {
    fontSize: 8.5,
    fontFamily: "Lora",
    color: "#4f5e7b",
    lineHeight: 1.4
  },
  // Terms label
  termsLabel: {
    fontSize: 9,
    fontFamily: "Montserrat",
    fontWeight: "bold",
    color: "#0f172a",
    marginTop: 12,
    marginBottom: 4
  },
  // Section heading row
  sectionRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 8,
    marginBottom: 4
  },
  sectionNumber: {
    fontSize: 9,
    fontFamily: "Montserrat",
    fontWeight: "bold",
    color: "#1b3a8c",
    marginRight: 6,
    width: 14
  },
  sectionTitle: {
    fontSize: 9,
    fontFamily: "Montserrat",
    fontWeight: "bold",
    color: "#0f172a"
  },
  sectionRule: {
    flex: 1,
    height: 0.5,
    backgroundColor: "#cccccc",
    marginLeft: 8
  },
  // Clause rows
  clauseRow: {
    flexDirection: "row",
    marginTop: 5,
    marginBottom: 2
  },
  clauseNumber: {
    fontSize: 9,
    fontFamily: "Montserrat",
    color: "#525f7f",
    width: 26
  },
  clauseRightCol: { flex: 1 },
  clauseHeading: {
    fontSize: 9,
    fontFamily: "Lora",
    fontWeight: "bold",
    color: "#0f172a",
    marginBottom: 3
  },
  clauseBody: {
    fontSize: 9,
    fontFamily: "Lora",
    color: "#27272a",
    lineHeight: 1.5
  },
  // Definition block
  definitionsContainer: {
    marginLeft: 26,
    paddingLeft: 12,
    borderLeftWidth: 1.5,
    borderLeftColor: "#e2e8f0",
    marginBottom: 4
  },
  defRow: {
    marginBottom: 6,
    paddingRight: 4
  },
  defTerm: {
    fontSize: 9,
    fontFamily: "Lora",
    fontWeight: "bold",
    color: "#0f172a"
  },
  defBody: {
    fontSize: 9,
    fontFamily: "Lora",
    color: "#27272a",
    lineHeight: 1.5
  },
  // List items
  listItemRow: {
    flexDirection: "row",
    marginLeft: 26,
    marginBottom: 4
  },
  listMarker: {
    width: 10,
    fontSize: 9,
    fontFamily: "Montserrat",
    color: "#525f7f"
  },
  listText: {
    flex: 1,
    fontSize: 9,
    fontFamily: "Lora",
    color: "#27272a",
    lineHeight: 1.5
  },
  // Sub-list items
  subListRow: {
    flexDirection: "row",
    marginLeft: 38,
    marginBottom: 4
  },
  subListMarker: {
    width: 14,
    fontSize: 9,
    fontFamily: "Montserrat",
    color: "#525f7f"
  },
  subListText: {
    flex: 1,
    fontSize: 9,
    fontFamily: "Lora",
    color: "#27272a",
    lineHeight: 1.5
  },
  // Body text (below clause row, indented)
  bodyText: {
    fontSize: 9,
    fontFamily: "Lora",
    color: "#27272a",
    lineHeight: 1.5,
    marginLeft: 44,
    marginBottom: 3
  },
  // Footer
  footerLine: {
    position: "absolute",
    bottom: 48,
    left: 36,
    right: 36,
    height: 0.5,
    backgroundColor: "#cccccc"
  },
  footerLeft: {
    position: "absolute",
    bottom: 30,
    left: 36,
    fontSize: 7.5,
    fontFamily: "Montserrat",
    color: "#555555",
    textAlign: "left"
  },
  footerRight: {
    position: "absolute",
    bottom: 30,
    right: 36,
    width: 100,
    fontSize: 7.5,
    fontFamily: "Montserrat",
    color: "#555555",
    textAlign: "right"
  },
  // Signature section
  signatureSection: {
    marginTop: 12,
    paddingTop: 8,
    borderTopWidth: 0.75,
    borderTopColor: "#cccccc"
  },
  signatureHeader: {
    fontSize: 9,
    fontFamily: "Montserrat",
    fontWeight: "bold",
    color: "#111111",
    marginBottom: 2
  },
  signatureSubtitle: {
    fontSize: 9,
    fontFamily: "Montserrat",
    color: "#444444",
    marginBottom: 10,
    lineHeight: 1.3
  },
  signatureGrid: {
    flexDirection: "row",
    justifyContent: "space-between"
  },
  sigCol: { width: "48%" },
  sigPartyLabel: {
    fontSize: 7.5,
    fontFamily: "Montserrat",
    color: "#888888",
    marginBottom: 10
  },
  ctbBadgeRow: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#f8f9fa",
    borderBottomWidth: 1.5,
    borderBottomColor: "#8898aa",
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 10,
    height: 64
  },
  ctbBadgeIcon: {
    marginRight: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  ctbBadgeLabelCol: { flexDirection: "column" },
  ctbBadgeTitle: {
    fontSize: 9,
    fontFamily: "Montserrat",
    fontWeight: "bold",
    color: "#4caf50",
    marginBottom: 2
  },
  ctbBadgeSub: {
    fontSize: 7.5,
    fontFamily: "Montserrat",
    color: "#525f7f"
  },
  sigImageArea: {
    height: 64,
    backgroundColor: "#f8f9fa",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 10,
    borderBottomWidth: 1.5,
    borderBottomColor: "#8898aa",
    paddingHorizontal: 10
  },
  sigImage: { maxWidth: 220, maxHeight: 52, objectFit: "contain" },
  sigNameScript: {
    fontSize: 32,
    fontFamily: "Times-BoldItalic",
    color: "#1b3a8c"
  },
  sigDetailRow: {
    flexDirection: "row",
    marginBottom: 4
  },
  sigDetailLabel: {
    fontSize: 8.5,
    fontFamily: "Montserrat",
    color: "#8898aa",
    width: 65
  },
  sigDetailValue: {
    fontSize: 8.5,
    fontFamily: "Montserrat",
    fontWeight: "bold",
    color: "#27272a",
    flex: 1
  },
  sigDetailValueNormal: {
    fontSize: 8.5,
    fontFamily: "Montserrat",
    color: "#525f7f",
    flex: 1
  }
});

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const SectionHeading = ({ num, title }: { num: string; title: string }) => (
  <View style={{ marginTop: 14, marginBottom: 8 }}>
    <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 4 }}>
      <Text style={styles.sectionNumber}>{num}</Text>
      <Text style={styles.sectionTitle}>{title}</Text>
    </View>
    <View style={{ height: 0.5, backgroundColor: "#e2e8f0" }} />
  </View>
);

const ClauseRow = ({
  num,
  heading,
  children
}: {
  num: string;
  heading: string;
  children?: React.ReactNode;
}) => (
  <View style={styles.clauseRow}>
    <Text style={styles.clauseNumber}>{num}</Text>
    <View style={styles.clauseRightCol}>
      <Text style={styles.clauseHeading}>{heading}</Text>
      {children}
    </View>
  </View>
);

const ListItem = ({ marker, children, style }: { marker: string; children: React.ReactNode; style?: any }) => (
  <View style={[styles.listItemRow, style]}>
    {marker === "▲" ? (
      <View style={{ width: 16, paddingTop: 3.5, display: "flex", alignItems: "flex-start" }}>
        <Svg viewBox="0 0 10 10" style={{ width: 5, height: 5 }}>
          <Path d="M5 1 L9 9 L1 9 Z" fill="#525f7f" />
        </Svg>
      </View>
    ) : (
      <Text style={styles.listMarker}>{marker}</Text>
    )}
    <Text style={styles.listText}>{children}</Text>
  </View>
);

const SubListItem = ({ marker, children }: { marker: string; children: React.ReactNode }) => (
  <View style={styles.subListRow}>
    <Text style={styles.subListMarker}>{marker}</Text>
    <Text style={styles.subListText}>{children}</Text>
  </View>
);

const DefEntry = ({ term, body }: { term: string; body: string }) => (
  <View style={styles.defRow}>
    <Text style={styles.defBody}>
      <Text style={styles.defTerm}>{term} </Text>
      {body}
    </Text>
  </View>
);

const wrapLongText = (text: string, maxChars = 22): string => {
  if (!text) return "";
  return text
    .split(" ")
    .map((word) => {
      if (word.length <= maxChars) return word;
      const chunks: string[] = [];
      for (let i = 0; i < word.length; i += maxChars) chunks.push(word.slice(i, i + maxChars));
      return chunks.join("\u200B");
    })
    .join(" ");
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface NdaPdfDocumentProps {
  clientName: string;
  clientAddress?: string;
  signerName?: string;
  signerAddress?: string;
  signatureBase64?: string | null;
  signedAt?: string | null;
  ssSignerName?: string | null;
  ssSignatureBase64?: string | null;
  ssSignedAt?: string | null;
  ndaNumber?: string;
  signerCompanyName?: string;
  sessionId?: string;
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------
export const NdaPdfDocument: React.FC<NdaPdfDocumentProps> = ({
  clientName,
  clientAddress,
  signerName,
  signerAddress,
  signatureBase64,
  signedAt,
  ssSignedAt,
  ndaNumber,
  signerCompanyName,
  sessionId
}) => {
  const fmtDate = (d?: string | null) =>
    d
      ? new Date(d).toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" })
      : new Date().toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" });

  const effectiveDate = fmtDate(signedAt);
  const ssDate = fmtDate(ssSignedAt || signedAt);
  const docNumber =
    ndaNumber ||
    `NDA-${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}${String(
      new Date().getDate()
    ).padStart(2, "0")}${sessionId ? `-${sessionId.slice(0, 8).toUpperCase()}` : ""}`;
  const footerCenter = `${docNumber} - Capture The Bug Limited & ${clientName || "Client"}`;

  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* Fixed header */}
        <View style={styles.header} fixed>
          <View style={styles.headerLeft}>
            <Image src={LOGO_SRC} style={styles.logoImage} />
            <Text style={styles.ndaNumber}>{docNumber}</Text>
          </View>
          <Text style={styles.confidentialBadge}>Confidential</Text>
        </View>

        {/* Fixed footer */}
        <View style={styles.footerLine} fixed />
        <Text style={styles.footerLeft} fixed>{footerCenter}</Text>
        <Text
          style={styles.footerRight}
          render={({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) =>
            totalPages ? `Page ${pageNumber} of ${totalPages}` : `Page ${pageNumber}`
          }
          fixed
        />

        {/* Title block */}
        <Text style={styles.mutualUndertaking}>Mutual undertaking</Text>
        <Text style={styles.mainTitle}>Non-Disclosure Agreement</Text>
        <Text style={styles.titleSubtitle}>
          Safely explore a potential cybersecurity partnership and define the penetration
          testing scope while protecting both parties{"\u2019"} proprietary information and strategic plans.
        </Text>

        {/* Metadata: date | agreement type | governing law */}
        <View style={styles.metaTableRow}>
          <View style={styles.metaTableCell}>
            <Text style={styles.metaSmallLabel}>Effective date</Text>
            <Text style={styles.metaValue}>{effectiveDate}</Text>
          </View>
          <View style={styles.metaCellDivider}>
            <Text style={styles.metaSmallLabel}>Agreement type</Text>
            <Text style={styles.metaValue}>Mutual confidentiality</Text>
          </View>
          <View style={styles.metaCellDivider}>
            <Text style={styles.metaSmallLabel}>Governing law</Text>
            <Text style={styles.metaValue}>New Zealand</Text>
          </View>
        </View>

        {/* Party block */}
        <View style={styles.partyRow}>
          <View style={styles.partyCol}>
            <Text style={styles.partySmallLabel}>Party 1 — discloser / recipient</Text>
            <Text style={styles.partyName}>Capture The Bug Limited</Text>
            <Text style={styles.partyDetail}>NZBN 9429051229467</Text>
            <Text style={styles.partyDetail}>526 Victoria Street, Hamilton Central, Hamilton 3204</Text>
          </View>
          <View style={styles.partyColRight}>
            <Text style={styles.partySmallLabel}>Party 2 — discloser / recipient</Text>
            <Text style={styles.partyName}>{wrapLongText(signerCompanyName || clientName || "Client", 28)}</Text>
            {signerName && (
              <Text style={styles.partyDetail}>{wrapLongText(signerName, 35)}</Text>
            )}
            <Text style={styles.partyDetail}>{wrapLongText(clientAddress || "Client address", 45)}</Text>
          </View>
        </View>

        {/* Preamble Agreement statement */}
        <Text style={[styles.termsLabel, { marginTop: 18, marginBottom: 4 }]}>Agreement</Text>
        <Text style={styles.titleSubtitle}>
          Each party agrees to keep confidential all Confidential Information disclosed to it by the other party on the terms of the Agreement.
        </Text>

        {/* Terms */}
        <Text style={styles.termsLabel}>Terms of the agreement</Text>

        {/* Section 1 */}
        <SectionHeading num="1" title="Interpretation" />

        <ClauseRow num="1.1" heading="Definitions">
          <Text style={styles.clauseBody}>In the Agreement, the following terms have the stated meaning:</Text>
        </ClauseRow>

        <View style={styles.definitionsContainer}>
          <DefEntry term="Affiliate" body="in relation to a party, a Related Company, director, officer, shareholder, owner, employee or professional advisor of that party." />
          <DefEntry term="Agreement :" body="this document, including the cover page, clauses 1 to 9, and the signatures." />
          <View style={styles.defRow}>
            <Text style={styles.defBody}>
              <Text style={styles.defTerm}>Confidential Information</Text>{" "}includes:
            </Text>
          </View>

          <ListItem marker="–" style={{ marginLeft: 8 }}>all information (whether in oral, written, electronic or other recorded form) disclosed by or on behalf of a party to the other party in connection with the Purpose, whether before or after the date of the Agreement;</ListItem>
          <ListItem marker="–" style={{ marginLeft: 8 }}>all copies of that information; and</ListItem>
          <ListItem marker="–" style={{ marginLeft: 8 }}>the fact that discussions are taking place related to the Purpose,</ListItem>
          <Text style={[styles.bodyText, { marginLeft: 8 }]}>but does not include:</Text>
          <ListItem marker="–" style={{ marginLeft: 8 }}>publicly available information; or</ListItem>
          <ListItem marker="–" style={{ marginLeft: 8 }}>information which becomes known to the other party from a third person who is not in breach of any obligation of confidentiality to the party who disclosed the information;</ListItem>
          <ListItem marker="–" style={{ marginLeft: 8 }}>information already in the other party's possession, or known to it, prior to disclosure by the disclosing party, without any obligation of confidentiality; or</ListItem>
          <ListItem marker="–" style={{ marginLeft: 8 }}>information independently developed by the other party without use of, or reference to, the disclosing party's Confidential Information.</ListItem>

          <DefEntry term="Purpose :" body="safely explore a potential cybersecurity partnership and define the penetration testing scope while protecting both parties' proprietary information and strategic plans." />
          <DefEntry term="Related Company :" body="has the meaning given in the New Zealand Companies Act 1993, adapted as required to apply to any company regardless of the jurisdiction of its incorporation." />
          <DefEntry term="Unauthorised Activity :" body="any unauthorised access, use, copying or disclosure of Confidential Information." />
        </View>

        <ClauseRow num="1.2" heading="Interpretation">
          <Text style={styles.clauseBody}>In the Agreement:</Text>
        </ClauseRow>
        <ListItem marker="a">clause and other headings are for ease of reference only and do not affect the interpretation of the Agreement;</ListItem>
        <ListItem marker="b">words in the singular include the plural and vice versa; and</ListItem>
        <ListItem marker="c">a reference to:</ListItem>
        <SubListItem marker="i">a party to the Agreement includes that party{"\u2019"}s permitted assigns;</SubListItem>
        <SubListItem marker="ii">including and similar words do not imply any limit; and</SubListItem>
        <SubListItem marker="iii">a person includes an individual, body corporate, unincorporated body or other entity.</SubListItem>

        <ClauseRow num="1.3" heading="No obligation">
          <Text style={styles.clauseBody}>Nothing in the Agreement obliges a party to disclose Confidential Information to the other party.</Text>
        </ClauseRow>

        {/* Section 2 */}
        <SectionHeading num="2" title="Use and disclosure" />

        <ClauseRow num="2.1" heading="Use">
          <Text style={styles.clauseBody}>Each party must use the other party{"\u2019"}s Confidential Information solely for the Purpose.</Text>
        </ClauseRow>

        <ClauseRow num="2.2" heading="Disclosure">
          <Text style={styles.clauseBody}>A party must not disclose the other party{"\u2019"}s Confidential Information to any person except an Affiliate who has:</Text>
        </ClauseRow>
        <ListItem marker="a">a need to know for the Purpose, but only to the extent of that need to know; and</ListItem>
        <ListItem marker="b">agreed to comply with obligations the same or substantially similar to those under the Agreement.</ListItem>

        <ClauseRow num="2.3" heading="Breach by Affiliate">
          <Text style={styles.clauseBody}>A breach of the Agreement by an Affiliate of a party is deemed to be a breach of the Agreement by that party.</Text>
        </ClauseRow>

        <ClauseRow num="2.4" heading="Onsupply">
          <Text style={styles.clauseBody}>The exception in clause 2.2 does not extend to enable an Affiliate to disclose Confidential Information to its affiliates.</Text>
        </ClauseRow>

        {/* Section 3 */}
        <SectionHeading num="3" title="Confidentiality" />

        <ClauseRow num="3.1" heading="Acknowledgement">
          <Text style={styles.clauseBody}>Each party acknowledges that the other party{"\u2019"}s Confidential Information is provided on terms of strict confidentiality.</Text>
        </ClauseRow>

        <ClauseRow num="3.2" heading="Security">
          <Text style={styles.clauseBody}>Each party must:</Text>
        </ClauseRow>
        <ListItem marker="a">establish and maintain proper and effective security measures to safeguard the other party{"\u2019"}s Confidential Information at all times from Unauthorised Activity;</ListItem>
        <ListItem marker="b">subject to any express clause to the contrary in the Agreement, keep the other party{"\u2019"}s Confidential Information under its control;</ListItem>
        <ListItem marker="c">immediately notify the other party of any suspected or actual Unauthorised Activity relating to the other party{"\u2019"}s Confidential Information, including by an Affiliate; and</ListItem>
        <ListItem marker="d">take all steps to stop any Unauthorised Activity, including providing all assistance that is reasonably requested by the other party in relation to any proceedings that the other party takes against any person for Unauthorised Activity.</ListItem>

        {/* Section 4 */}
        <SectionHeading num="4" title="Disclosure required by law" />

        <ClauseRow num="4.1" heading="Types of disclosure">
          <Text style={styles.clauseBody}>Clause 4.2 applies if a party is obliged to disclose the other party{"\u2019"}s Confidential Information by:</Text>
        </ClauseRow>
        <ListItem marker="a">law (including under the New Zealand Official Information Act 1982 or the rules of any stock exchange to which the relevant party is subject); or</ListItem>
        <ListItem marker="b">a court order.</ListItem>

        <ClauseRow num="4.2" heading="Action to take">
          <Text style={styles.clauseBody}>The party must:</Text>
        </ClauseRow>
        <ListItem marker="a">promptly, and prior to disclosure, give written notice of the requirement to the other party to enable the other party to seek a protective order or other appropriate remedy; and</ListItem>
        <ListItem marker="b">where the other party{"\u2019"}s Confidential Information is disclosed as a result of the requirement, use all reasonable endeavours to obtain, from the recipient, assurances that the Confidential Information will be treated as confidential.</ListItem>

        {/* Section 5 */}
        <SectionHeading num="5" title="Breach" />

        <View style={styles.clauseRow}>
          <Text style={styles.clauseNumber}>5.1</Text>
          <View style={styles.clauseRightCol}>
            <Text style={styles.clauseBody}>Each party acknowledges that in the case of a breach of the Agreement by it or an Affiliate:</Text>
          </View>
        </View>
        <ListItem marker="a">the breach may result in loss to the other party that may not be adequately compensated by payment of damages; and</ListItem>
        <ListItem marker="b">the other party is entitled to equitable relief, including enforcing its rights by specific performance or injunction proceedings.</ListItem>

        {/* Section 6 */}
        <SectionHeading num="6" title="Disclaimer" />

        <ClauseRow num="6.1" heading="No warranty">
          <Text style={styles.clauseBody}>Neither party makes or gives any express or implied representation, undertaking or warranty that:</Text>
        </ClauseRow>
        <ListItem marker="a">the Confidential Information is or will be:</ListItem>
        <SubListItem marker="i">complete or accurate; or</SubListItem>
        <SubListItem marker="ii">audited or independently verified; or</SubListItem>
        <ListItem marker="b">reasonable care has been or will be taken in compiling or preparing the Confidential Information.</ListItem>

        <ClauseRow num="6.2" heading="No liability for use">
          <Text style={styles.clauseBody}>A party will not be liable to the other party (or the other party{"\u2019"}s Affiliates) in respect of its Confidential Information or the use of that Confidential Information.</Text>
        </ClauseRow>

        {/* Section 7 */}
        <SectionHeading num="7" title="Ownership" />

        <Text style={[styles.clauseBody, { marginTop: 4, marginBottom: 8 }]}>
          Each party retains all intellectual property rights, title and interest in the Confidential Information that it discloses.
        </Text>

        {/* Section 8 */}
        <SectionHeading num="8" title="Completion of purpose" />

        <ClauseRow num="8.1" heading="Obligations continue">
          <Text style={styles.clauseBody}>The obligations under the Agreement continue to apply to the parties after completion or termination of the Purpose.</Text>
        </ClauseRow>

        <ClauseRow num="8.2" heading="Return of information" />
        <ListItem marker="a">At the request of a party after completion or termination of the Purpose, the other party must promptly return to it or destroy all Confidential Information which is in the party{"\u2019"}s, or its Affiliates{"\u2019"}, possession or control.</ListItem>
        <ListItem marker="b">Despite clause 8.2a, a party, or any of its professional advisors, may retain Confidential Information that it is legally required to retain, but only to the extent of that legal requirement.</ListItem>
        <ListItem marker="c">The other party must provide the first party with written confirmation that it has complied with clauses 8.2a and 8.2b.</ListItem>

        {/* Section 9 */}
        <SectionHeading num="9" title="General" />

        <ClauseRow num="9.1" heading="Waiver">
          <Text style={styles.clauseBody}>To waive a right under the Agreement, the waiver must be in writing and signed by the waiving party.</Text>
        </ClauseRow>

        <ClauseRow num="9.2" heading="Assignment">
          <Text style={styles.clauseBody}>Neither party may assign or otherwise transfer any of its rights or obligations under the Agreement unless with the other party{"\u2019"}s prior written consent.</Text>
        </ClauseRow>

        <ClauseRow num="9.3" heading="Law">
          <Text style={styles.clauseBody}>The Agreement is governed by, and must be interpreted in accordance with, the laws of New Zealand. Each party submits to the non-exclusive jurisdiction of the Courts of New Zealand in relation to any dispute connected with the Agreement.</Text>
        </ClauseRow>

        <ClauseRow num="9.4" heading="Severability">
          <Text style={styles.clauseBody}>If any provision of the Agreement is, or becomes, unenforceable, illegal or invalid for any reason, the relevant provision is deemed to be varied to the extent necessary to remedy the unenforceability, illegality or invalidity. If variation is not possible, the provision must be treated as severed from the Agreement without affecting any other provisions of the Agreement.</Text>
        </ClauseRow>

        <ClauseRow num="9.5" heading="Counterparts">
          <Text style={styles.clauseBody}>The Agreement may be signed in counterparts, each of which constitutes an original and all of which constitute the same agreement. A party may enter the Agreement by signing and sending (including by facsimile or email) a counterpart copy to the other party.</Text>
        </ClauseRow>

        <ClauseRow num="9.6" heading="Variation">
          <Text style={styles.clauseBody}>Any variation to the Agreement must be in writing and signed by both parties.</Text>
        </ClauseRow>

        <ClauseRow num="9.7" heading="Further acts">
          <Text style={styles.clauseBody}>Each party must sign and deliver any document and undertake any act, matter and thing which is reasonably requested by the other party to give effect to the intent and purpose of the Agreement.</Text>
        </ClauseRow>

        <ClauseRow num="9.8" heading="Entire agreement">
          <Text style={styles.clauseBody}>The Agreement sets out everything agreed by the parties relating to its subject matter and supersedes and cancels anything discussed, exchanged or agreed prior to the date of the Agreement. The parties have not relied on any representation, warranty or agreement relating to the subject matter of the Agreement that is not expressly set out in the Agreement, and no such representation, warranty or agreement has any effect from the date of the Agreement.</Text>
        </ClauseRow>

        {/* Signature section */}
        <View style={styles.signatureSection} wrap={false}>
          <Text style={styles.signatureHeader}>Execution of agreement</Text>
          <Text style={styles.signatureSubtitle}>
            Signed by the duly authorised representatives of each party as of the
            effective date.
          </Text>
          <View style={styles.signatureGrid}>
            {/* CTB */}
            <View style={styles.sigCol}>
              <Text style={styles.sigPartyLabel}>Party 1 — Capture The Bug Limited</Text>
              <View style={styles.ctbBadgeRow}>
                <View style={styles.ctbBadgeIcon}>
                  <Svg viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
                    <Path
                      d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
                      fill="none"
                      stroke="#4caf50"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <Path
                      d="m9 12 2 2 4-4"
                      fill="none"
                      stroke="#4caf50"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </Svg>
                </View>
                <View style={styles.ctbBadgeLabelCol}>
                  <Text style={styles.ctbBadgeTitle}>Digitally signed & verified</Text>
                  <Text style={styles.ctbBadgeSub}>Capture The Bug Limited</Text>
                  <Text style={styles.ctbBadgeSub}>Corporate signatory</Text>
                </View>
              </View>
              <View style={styles.sigDetailRow}>
                <Text style={styles.sigDetailLabel}>Signatory</Text>
                <Text style={styles.sigDetailValue}>Ankita Dhakar</Text>
              </View>
              <View style={styles.sigDetailRow}>
                <Text style={styles.sigDetailLabel}>Title</Text>
                <Text style={styles.sigDetailValueNormal}>Authorised signatory</Text>
              </View>
              <View style={styles.sigDetailRow}>
                <Text style={styles.sigDetailLabel}>Date</Text>
                <Text style={styles.sigDetailValueNormal}>{ssDate}</Text>
              </View>
            </View>

            {/* Client */}
            <View style={styles.sigCol}>
              <Text style={styles.sigPartyLabel}>Party 2 — {wrapLongText(clientName || "Client", 30)}</Text>
              <View style={styles.sigImageArea}>
                {signatureBase64 ? (
                  <Image src={signatureBase64} style={styles.sigImage} />
                ) : (
                  <Text style={{ fontSize: 10, fontFamily: "Montserrat", color: "#888888" }}>Yet to be signed</Text>
                )}
              </View>
              <View style={styles.sigDetailRow}>
                <Text style={styles.sigDetailLabel}>Signatory</Text>
                <Text style={styles.sigDetailValue}>{signerName ? wrapLongText(signerName, 25) : "Yet to be signed"}</Text>
              </View>
              <View style={styles.sigDetailRow}>
                <Text style={styles.sigDetailLabel}>Company</Text>
                <Text style={styles.sigDetailValueNormal}>{signerCompanyName ? wrapLongText(signerCompanyName, 30) : (clientName || "Yet to be signed")}</Text>
              </View>
              <View style={styles.sigDetailRow}>
                <Text style={styles.sigDetailLabel}>Date</Text>
                <Text style={styles.sigDetailValueNormal}>{effectiveDate}</Text>
              </View>
            </View>
          </View>
        </View>

      </Page>
    </Document>
  );
};
